import { Button, Carousel, Grid, Space } from 'antd';
import { isEmpty } from 'lodash/fp';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import Image from '../../../../components/Image';
import LeftIcon from '../../../../icons/mobility/left.svg';
import RightIcon from '../../../../icons/mobility/right.svg';
import { type CarouselRef } from '../../../../types/antd';
import breakpoints from '../../../../utilities/breakpoints';
import usePublic from '../../../../utilities/usePublic';

const StyledCarousel = styled(Carousel)`
    & .slick-dots li,
    & .slick-dots li.slick-active {
        width: 10px;
        height: 10px;
        margin-bottom: 4px;

        & > button {
            width: 10px;
            height: 10px;
            border-radius: 25px;
            background: var(--ant-primary-color);
        }
    }

    .slick-dots li:not(.slick-active) button {
        background: #fff;
        opacity: 1;
    }
`;

const Container = styled.div`
    margin-bottom: 32px;
`;

const CarouselContainer = styled.div`
    position: relative;
`;

const ImageContainer = styled.div`
    & .ant-image {
        width: 100%;

        & > img {
            aspect-ratio: 16/9;
            object-fit: cover;
        }
    }
`;

const ThumbnailContainer = styled.div`
    overflow: auto;
    margin-top: 16px;

    & .ant-image > img {
        width: 180px;
        aspect-ratio: 16/9;
        object-fit: cover;
        cursor: pointer;
    }
`;

const StyledButton = styled(Button)<{ position: 'left' | 'right' }>`
    &.ant-btn {
        margin: 0;
    }

    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    color: #fff;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0;

    width: 32px;
    height: 32px;

    top: calc(50% - 16px);

    ${({ position }) => position === 'left' && 'left: 0;'}
    ${({ position }) => position === 'right' && 'right: 0;'}

    &:hover,
    &:focus,
    &:active {
        background-color: rgba(0, 0, 0, 0.7);
        border: none;
        color: #fff;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        &.ant-btn {
            margin: 0 16px;
        }
    }
`;

export type VehicleImagesProps = {
    sources: string[];
};

const VehicleImages = ({ sources }: VehicleImagesProps) => {
    const fallbackSrc = usePublic('empty.svg');
    const sliderRef = useRef<CarouselRef | undefined>();
    const screens = Grid.useBreakpoint();

    const actions = useMemo(
        () => ({
            next: () => sliderRef.current.next(),
            previous: () => sliderRef.current.prev(),
            goTo: (index: number) => sliderRef.current.goTo(index),
        }),
        []
    );

    return (
        <Container>
            <CarouselContainer>
                <StyledCarousel
                    ref={slider => {
                        sliderRef.current = slider;
                    }}
                    dots={!screens.md}
                >
                    {(isEmpty(sources) ? [fallbackSrc] : sources).map((source, index) => (
                        <ImageContainer>
                            <Image key={index.toString()} preview={false} src={source} />
                        </ImageContainer>
                    ))}
                </StyledCarousel>

                {!!sources.length && sources.length > 1 && (
                    <>
                        <StyledButton onClick={actions.next} position="right">
                            <RightIcon />
                        </StyledButton>
                        <StyledButton onClick={actions.previous} position="left">
                            <LeftIcon />
                        </StyledButton>
                    </>
                )}
            </CarouselContainer>
            {!isEmpty(sources) && screens.md && (
                <ThumbnailContainer>
                    <Space size={24}>
                        {sources.map((source, index) => (
                            <Image
                                key={`thumbnail-${index.toString()}`}
                                onClick={() => actions.goTo(index)}
                                preview={false}
                                src={source}
                            />
                        ))}
                    </Space>
                </ThumbnailContainer>
            )}
        </Container>
    );
};

export default VehicleImages;
